// src/components/resources/calculators/MarketingBudgetCalculator.tsx

'use client'

import { useState, useMemo, useCallback } from 'react'
import Link from 'next/link'
import { cn } from '~/lib/utils'
import { formatCurrency } from '~/lib/utils/formatters'
import { downloadCsv } from '~/lib/utils/csv'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { FAQ } from '~/components/shared/FAQ'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  ScaleIcon,
  ShoppingCartIcon,
  BuildingOffice2Icon,
  SparklesIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  UsersIcon,
  VideoCameraIcon,
  BeakerIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline'
import type { ComponentType, SVGProps } from 'react'
import { RELATED_GLOSSARY, RELATED_TOOLS } from './MarketingBudgetCalculator.related'
import {
  type Tier,
  type Channel,
  TIER_TARGETS,
  TIER_RANGES,
  TIER_ORDER,
  computeAnnualBudget,
  sumChannelPct,
  computeTierTotals,
  normalizeChannels,
  tierStatus,
  tierInRange,
  budgetHealth,
  clampNonNegative,
  clampPercent,
} from './MarketingBudgetCalculator.logic'

type IconType = ComponentType<SVGProps<SVGSVGElement>>

type PresetChannel = Omit<Channel, 'id'>

interface Preset {
  label: string
  /** One-line description shown on the preset card. */
  tagline: string
  /** Suggested marketing spend as a % of revenue for this business type. */
  revenuePctHint: string
  icon: IconType
  channels: PresetChannel[]
}

type PresetId = 'balanced' | 'ecommerce' | 'saas' | 'brand'

/**
 * Channel-mix presets. Each sums to 100%. Tier splits are intentionally
 * close to (but not slavishly) the 70/20/10 Core/Growth/Experimental
 * framework so the on-page health check has something real to flag.
 * Sources: Gartner CMO Spend 2025, Forrester B2B budget benchmarks,
 * Funnel.io + Improvado allocation guides (cited in the FAQ copy).
 *
 * Keyed by the finite `PresetId` union (not a string index signature) so
 * lookups are typed as defined under `noUncheckedIndexedAccess`.
 */
const PRESETS: Record<PresetId, Preset> = {
  balanced: {
    label: 'Balanced (70/20/10)',
    tagline: 'Core 70% · Growth 20% · Experimental 10%',
    revenuePctHint: '8–12% of revenue',
    icon: ScaleIcon,
    channels: [
      { name: 'Google Search', tier: 'core', pct: 30 },
      { name: 'Meta Ads', tier: 'core', pct: 25 },
      { name: 'Email / CRM', tier: 'core', pct: 15 },
      { name: 'TikTok / Short Video', tier: 'growth', pct: 12 },
      { name: 'SEO / Content', tier: 'growth', pct: 8 },
      { name: 'Experimental / Testing', tier: 'experimental', pct: 10 },
    ],
  },
  ecommerce: {
    label: 'B2C Ecommerce',
    tagline: 'Growth-heavy for online stores',
    revenuePctHint: '10–20% of revenue',
    icon: ShoppingCartIcon,
    channels: [
      { name: 'Meta Ads', tier: 'core', pct: 30 },
      { name: 'Google Shopping / PMax', tier: 'core', pct: 25 },
      { name: 'Email / SMS', tier: 'core', pct: 10 },
      { name: 'TikTok Ads', tier: 'growth', pct: 13 },
      { name: 'Influencer / UGC', tier: 'growth', pct: 7 },
      { name: 'Creative Testing', tier: 'experimental', pct: 15 },
    ],
  },
  saas: {
    label: 'B2B SaaS (Demand Gen)',
    tagline: 'Demand gen & long sales cycles',
    revenuePctHint: '6–15% of revenue (15–30% early-stage)',
    icon: BuildingOffice2Icon,
    channels: [
      { name: 'Google Search', tier: 'core', pct: 24 },
      { name: 'LinkedIn Ads', tier: 'core', pct: 20 },
      { name: 'Content / SEO', tier: 'core', pct: 16 },
      { name: 'Webinars / Events', tier: 'growth', pct: 12 },
      { name: 'Retargeting', tier: 'growth', pct: 8 },
      { name: 'New Channels / Testing', tier: 'experimental', pct: 20 },
    ],
  },
  brand: {
    label: 'Brand + Performance',
    tagline: 'Balance brand building & performance',
    revenuePctHint: '8–15% of revenue',
    icon: SparklesIcon,
    channels: [
      { name: 'Paid Search', tier: 'core', pct: 25 },
      { name: 'Paid Social', tier: 'core', pct: 20 },
      { name: 'Email / CRM', tier: 'core', pct: 15 },
      { name: 'YouTube / CTV (Brand)', tier: 'growth', pct: 15 },
      { name: 'PR / Influencer', tier: 'growth', pct: 10 },
      { name: 'Experimental / Testing', tier: 'experimental', pct: 15 },
    ],
  },
}

// Presentation metadata per tier. Targets live in the logic module
// (TIER_TARGETS) so the framework numbers have a single source of truth.
const TIER_META: Record<Tier, { label: string; short: string; color: string; bar: string }> = {
  core: { label: 'Core (proven)', short: 'Core', color: 'text-indigo-600 dark:text-indigo-400', bar: 'bg-indigo-500' },
  growth: { label: 'Growth (scaling)', short: 'Growth', color: 'text-indigo-500 dark:text-indigo-300', bar: 'bg-indigo-400' },
  experimental: { label: 'Experimental (testing)', short: 'Experimental', color: 'text-indigo-400 dark:text-indigo-200', bar: 'bg-indigo-300' },
}

const FAQ_ITEMS = [
  {
    question: 'How should I allocate my marketing budget across channels?',
    answer:
      "There's no universal split, but a durable starting framework is 70/20/10: 70% to proven core channels, 20% to growth channels you're scaling, and 10% to experiments. From there, rank channels by ROAS (or CAC for lead-gen) and shift budget toward what performs. This tool lets you start from a business-type preset and adjust.",
  },
  {
    question: 'What percentage of revenue should go to marketing?',
    answer:
      'Benchmarks vary by model and stage. B2B companies typically spend ~6–10% of revenue (Gartner put the 2025 average near 7.7%), B2C/product brands ~10–15%, DTC ecommerce ~10–20%, and early-stage SaaS often 15–30% while mature SaaS settles to ~5–7%. Use the "% of revenue" method to apply a benchmark to your own revenue.',
  },
  {
    question: 'What is the 70/20/10 marketing budget rule?',
    answer:
      'It splits spend into 70% Core (proven, scaled channels), 20% Growth (channels showing promise that you\'re scaling), and 10% Experimental (new platforms, formats, and tests). It balances reliable returns with funded experimentation. The tool shows your live Core/Growth/Experimental split against these targets.',
  },
  {
    question: 'Should I budget on monthly or annual figures?',
    answer:
      'Plan annually for the big picture, then phase monthly to manage pacing and seasonality. This calculator shows both annual and monthly figures per channel, and the CSV export includes both so you can drop them straight into a tracker.',
  },
  {
    question: 'Is this marketing budget calculator free?',
    answer:
      'Yes — it runs entirely in your browser, requires no sign-up, and lets you export your allocation to CSV at no cost. Pair it with our downloadable Marketing Budget Template to track planned vs. actual spend.',
  },
]

let channelIdSeq = 0
function withIds(channels: PresetChannel[]): Channel[] {
  return channels.map((c) => ({ ...c, id: `ch-${channelIdSeq++}` }))
}

// Keyword → icon for the channel rows. First match wins, so order
// specific rules before broad ones; anything unmatched (Meta, Paid
// Social, LinkedIn, …) falls back to the megaphone.
const CHANNEL_ICON_RULES: ReadonlyArray<{ test: RegExp; icon: IconType }> = [
  { test: /search|\bsem\b|\bppc\b|shopping|pmax/i, icon: MagnifyingGlassIcon },
  { test: /email|\bsms\b|\bcrm\b|newsletter/i, icon: EnvelopeIcon },
  { test: /content|\bseo\b|blog|organic/i, icon: DocumentTextIcon },
  { test: /video|youtube|\bctv\b|reel|short/i, icon: VideoCameraIcon },
  { test: /influencer|\bugc\b|\bpr\b|partnership|affiliate/i, icon: UsersIcon },
  { test: /retarget|remarket/i, icon: ArrowPathIcon },
  { test: /test|experiment|new channel/i, icon: BeakerIcon },
]
function channelIcon(name: string): IconType {
  return CHANNEL_ICON_RULES.find((rule) => rule.test.test(name))?.icon ?? MegaphoneIcon
}

const TIER_STROKE: Record<Tier, string> = {
  core: '#6366f1',
  growth: '#818cf8',
  experimental: '#c7d2fe',
}

/**
 * Semicircular gauge: three arcs sized by the Core/Growth/Experimental
 * share of the allocated budget, with an on-track / tune-your-mix verdict in
 * the well. `pathLength={100}` normalizes the arc so segment lengths are just
 * percentages — no trig.
 */
function HealthGauge({
  tierTotals,
  totalPct,
}: {
  tierTotals: Record<Tier, number>
  totalPct: number
}): JSX.Element {
  const health = budgetHealth(tierTotals, totalPct)
  const denom = totalPct > 0 ? totalPct : 1
  const arc = 'M 14 66 A 52 52 0 0 1 118 66'
  // Cumulative offsets computed purely — react-hooks/immutability flags mutating a
  // captured `start` during render. TIER_ORDER is tiny, so the O(n²) prefix sum is fine.
  const segments = TIER_ORDER.map((tier, i) => {
    const len = (tierTotals[tier] / denom) * 100
    const start = TIER_ORDER.slice(0, i).reduce(
      (sum, t) => sum + (tierTotals[t] / denom) * 100,
      0,
    )
    return { tier, len, start }
  })

  return (
    <div className="relative mx-auto w-[160px]">
      <svg viewBox="0 0 132 80" className="w-full" role="img" aria-label={`Budget health: ${health.label}`}>
        <path d={arc} fill="none" stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth={12} strokeLinecap="round" pathLength={100} />
        {segments.map((s) =>
          s.len > 0 ? (
            <path
              key={s.tier}
              d={arc}
              fill="none"
              stroke={TIER_STROKE[s.tier]}
              strokeWidth={12}
              strokeLinecap="round"
              pathLength={100}
              strokeDasharray={`${Math.max(0, s.len - 2)} 100`}
              strokeDashoffset={-s.start}
            />
          ) : null,
        )}
      </svg>
      <div className="absolute inset-x-0 bottom-1 flex flex-col items-center">
        {health.onTrack && <CheckCircleIcon className="h-6 w-6 text-emerald-500" aria-hidden="true" />}
        <span className={cn('text-sm font-semibold', health.onTrack ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>
          {health.label}
        </span>
      </div>
    </div>
  )
}

export function MarketingBudgetCalculator(): JSX.Element {
  const [presetId, setPresetId] = useState<PresetId>('balanced')
  const [method, setMethod] = useState<'total' | 'revenue'>('total')
  const [period, setPeriod] = useState<'annual' | 'monthly'>('annual')
  const [budgetInput, setBudgetInput] = useState<number>(120000)
  const [revenue, setRevenue] = useState<number>(1_000_000)
  const [revenuePct, setRevenuePct] = useState<number>(10)
  const [channels, setChannels] = useState<Channel[]>(() => withIds(PRESETS.balanced.channels))

  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  const annualBudget = useMemo(
    () => computeAnnualBudget({ method, period, budgetInput, revenue, revenuePct }),
    [method, revenue, revenuePct, period, budgetInput],
  )

  const monthlyBudget = annualBudget / 12

  const totalPct = useMemo(() => sumChannelPct(channels), [channels])

  const tierTotals = useMemo(() => computeTierTotals(channels), [channels])

  const applyPreset = useCallback(
    (id: PresetId) => {
      setPresetId(id)
      setChannels(withIds(PRESETS[id].channels))
      sendResourceEvent('interact', {
        resourceType: 'calculator',
        resourceSlug: 'marketing-budget-calculator',
        engagementMs: getDeltaMs(),
        params: { interaction_type: 'preset', preset: id },
      })
    },
    [getDeltaMs],
  )

  const updateChannel = useCallback((id: string, patch: Partial<Channel>) => {
    setChannels((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }, [])

  const removeChannel = useCallback((id: string) => {
    setChannels((prev) => prev.filter((c) => c.id !== id))
  }, [])

  const addChannel = useCallback(() => {
    setChannels((prev) => [...prev, { id: `ch-${channelIdSeq++}`, name: 'New channel', tier: 'growth', pct: 0 }])
  }, [])

  const normalize = useCallback(() => {
    setChannels((prev) => normalizeChannels(prev))
  }, [])

  const reset = useCallback(() => {
    setPresetId('balanced')
    setMethod('total')
    setPeriod('annual')
    setBudgetInput(120000)
    setRevenue(1_000_000)
    setRevenuePct(10)
    setChannels(withIds(PRESETS.balanced.channels))
  }, [])

  const handleExport = useCallback(() => {
    const rows: (string | number)[][] = [
      ['AdSights Marketing Budget Allocation'],
      [`Preset: ${PRESETS[presetId].label}`, `Annual budget: ${formatCurrency(annualBudget, 0)}`],
      [],
      ['Channel', 'Tier', '% of Budget', 'Annual Budget', 'Monthly Budget'],
      ...channels.map((c) => [
        c.name,
        TIER_META[c.tier].short,
        `${c.pct}%`,
        Math.round((annualBudget * c.pct) / 100),
        Math.round((annualBudget * c.pct) / 100 / 12),
      ]),
      [],
      ['Total allocated', '', `${totalPct}%`, Math.round((annualBudget * totalPct) / 100), Math.round((annualBudget * totalPct) / 100 / 12)],
      [],
      ['Tier', 'Allocation %', 'Target %'],
      ...TIER_ORDER.map((t) => [TIER_META[t].short, `${tierTotals[t]}%`, `${TIER_TARGETS[t]}%`]),
    ]
    downloadCsv('adsights-marketing-budget-allocation', rows)
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'marketing-budget-calculator',
      engagementMs: getTotalMs(),
      params: { outcome: 'export_csv', annual_budget: annualBudget, channels: channels.length, total_pct: totalPct },
    })
  }, [presetId, annualBudget, channels, totalPct, tierTotals, getTotalMs])

  const allocatedPctClamped = Math.min(totalPct, 100)
  const overAllocated = totalPct > 100
  const inputClasses =
    'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white'

  return (
    <div className="mx-auto max-w-5xl" role="main" aria-labelledby="budget-builder-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Header */}
        <div className="px-6 pt-8 sm:px-8">
          <h2 id="budget-builder-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Marketing Budget Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Allocate your marketing budget across channels, check it against the 70/20/10 framework, and export your plan.
          </p>
        </div>

        {/* Presets */}
        <div className="mt-6 px-6 sm:px-8">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Start from a preset <span className="font-normal text-gray-400">(optional)</span>
          </span>
          <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {(Object.keys(PRESETS) as PresetId[]).map((id) => {
              const preset = PRESETS[id]
              const Icon = preset.icon
              const selected = presetId === id
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => applyPreset(id)}
                  aria-pressed={selected}
                  className={cn(
                    'flex flex-col gap-1.5 rounded-xl border p-3 text-left transition-colors',
                    selected
                      ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 dark:border-indigo-400 dark:bg-indigo-900/20 dark:ring-indigo-400'
                      : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800',
                  )}
                >
                  <Icon className={cn('h-5 w-5', selected ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400')} aria-hidden="true" />
                  <span className={cn('text-sm font-semibold leading-tight', selected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-900 dark:text-white')}>
                    {preset.label}
                  </span>
                  <span className="text-xs leading-snug text-gray-500 dark:text-gray-400">{preset.tagline}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Budget inputs */}
        <div className="mt-6 grid gap-6 px-6 sm:grid-cols-2 sm:px-8">
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Budget method</span>
            <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Budget method">
              <button
                type="button"
                onClick={() => setMethod('total')}
                aria-pressed={method === 'total'}
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm font-medium',
                  method === 'total'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:border-indigo-400 dark:bg-indigo-900/20 dark:text-indigo-400'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800',
                )}
              >
                Set total
              </button>
              <button
                type="button"
                onClick={() => setMethod('revenue')}
                aria-pressed={method === 'revenue'}
                className={cn(
                  'rounded-lg border px-3 py-2 text-sm font-medium',
                  method === 'revenue'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:border-indigo-400 dark:bg-indigo-900/20 dark:text-indigo-400'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800',
                )}
              >
                % of revenue
              </button>
            </div>
          </div>

          {method === 'total' ? (
            <div>
              <label htmlFor="total-budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Total budget ({period})
              </label>
              <div className="flex gap-2">
                <input
                  id="total-budget"
                  type="number"
                  min={0}
                  value={budgetInput}
                  onChange={(e) => setBudgetInput(clampNonNegative(+e.target.value))}
                  className={inputClasses}
                />
                <select
                  aria-label="Budget period"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as 'annual' | 'monthly')}
                  className="mt-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="annual">/ year</option>
                  <option value="monthly">/ month</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="revenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Annual revenue
                </label>
                <input
                  id="revenue"
                  type="number"
                  min={0}
                  value={revenue}
                  onChange={(e) => setRevenue(clampNonNegative(+e.target.value))}
                  className={inputClasses}
                />
              </div>
              <div>
                <label htmlFor="revenue-pct" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  % to marketing
                </label>
                <input
                  id="revenue-pct"
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={revenuePct}
                  onChange={(e) => setRevenuePct(clampPercent(+e.target.value))}
                  className={inputClasses}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Benchmark: {PRESETS[presetId].revenuePctHint}</p>
              </div>
            </div>
          )}
        </div>

        {/* Budget summary banner */}
        <div className="mt-6 px-6 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 rounded-xl bg-gray-50 p-4 dark:bg-gray-800/60">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Annual budget</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatCurrency(annualBudget, 0)}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{formatCurrency(monthlyBudget, 0)} / month</p>
            </div>
            <div className="text-right">
              <p className={cn('text-sm font-medium', overAllocated ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400')}>
                {totalPct}% allocated{overAllocated ? ' — over 100%' : totalPct < 100 ? ` — ${100 - totalPct}% unassigned` : ''}
              </p>
              {totalPct !== 100 && (
                <button type="button" onClick={normalize} className="mt-1 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400">
                  Normalize to 100%
                </button>
              )}
            </div>
          </div>

          {/* Stacked allocation bar */}
          <div className="mt-4" aria-hidden="true">
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              {channels.map((c) => (
                <div
                  key={c.id}
                  className={cn('h-full', TIER_META[c.tier].bar)}
                  style={{ width: `${overAllocated ? (c.pct / totalPct) * 100 : c.pct}%` }}
                  title={`${c.name}: ${c.pct}%`}
                />
              ))}
              {!overAllocated && allocatedPctClamped < 100 && (
                <div className="h-full bg-transparent" style={{ width: `${100 - allocatedPctClamped}%` }} />
              )}
            </div>
          </div>
        </div>

        {/* Channel editor */}
        <div className="mt-6 px-6 pb-2 sm:px-8">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Edit Channels &amp; Tiers
          </h3>
          {/* `relative` contains the header's position:absolute `sr-only`
              cell — without a positioned ancestor it escapes to the page and,
              sitting at its static x inside the wide (scrollable) table, pushes
              the document's horizontal scroll width past the viewport on
              mobile. */}
          <div className="relative overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <th scope="col" className="pb-2 pr-3 font-semibold">Channel</th>
                  <th scope="col" className="pb-2 pr-3 font-semibold">Tier</th>
                  <th scope="col" className="pb-2 pr-3 font-semibold">% of budget</th>
                  <th scope="col" className="pb-2 pr-3 text-right font-semibold">Annual</th>
                  <th scope="col" className="pb-2 pr-3 text-right font-semibold">Monthly</th>
                  <th scope="col" className="pb-2 font-semibold"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {channels.map((c) => {
                  const annual = (annualBudget * c.pct) / 100
                  const ChannelIcon = channelIcon(c.name)
                  return (
                    <tr key={c.id}>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            <ChannelIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                          <input
                            type="text"
                            aria-label="Channel name"
                            value={c.name}
                            onChange={(e) => updateChannel(c.id, { name: e.target.value })}
                            className="w-36 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                          />
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <select
                          aria-label="Channel tier"
                          value={c.tier}
                          onChange={(e) => updateChannel(c.id, { tier: e.target.value as Tier })}
                          className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        >
                          {TIER_ORDER.map((t) => (
                            <option key={t} value={t}>{TIER_META[t].label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            aria-label={`${c.name} percentage`}
                            min={0}
                            max={100}
                            step={1}
                            value={c.pct}
                            onChange={(e) => updateChannel(c.id, { pct: clampPercent(+e.target.value) })}
                            className="h-1.5 w-24 cursor-pointer accent-indigo-600"
                          />
                          <input
                            type="number"
                            aria-label={`${c.name} percentage value`}
                            min={0}
                            max={100}
                            value={c.pct}
                            onChange={(e) => updateChannel(c.id, { pct: clampPercent(+e.target.value) })}
                            className="w-16 rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                          />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(annual, 0)}</td>
                      <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-400">{formatCurrency(annual / 12, 0)}</td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeChannel(c.id)}
                          aria-label={`Remove ${c.name}`}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={addChannel}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            <PlusIcon className="h-4 w-4" /> Add channel
          </button>
        </div>

        {/* Budget health check — 70/20/10 gauge + per-tier bands */}
        <div className="mt-6 px-6 pb-6 sm:px-8">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Budget Health Check
          </h3>
          <div className="grid items-center gap-6 rounded-xl bg-gray-50 p-5 dark:bg-gray-800/60 sm:grid-cols-[160px,1fr]">
            <HealthGauge tierTotals={tierTotals} totalPct={totalPct} />
            <div className="space-y-2.5">
              {TIER_ORDER.map((t) => {
                const inRange = tierInRange(tierTotals[t], t)
                const [lo, hi] = TIER_RANGES[t]
                return (
                  <div key={t} className="flex items-center gap-3">
                    <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: TIER_STROKE[t] }} aria-hidden="true" />
                    <span className="w-28 text-sm font-medium text-gray-700 dark:text-gray-300">{TIER_META[t].short}</span>
                    <span className="w-12 text-sm font-bold tabular-nums text-gray-900 dark:text-white">{tierTotals[t]}%</span>
                    <span className="text-xs text-gray-400">{lo}–{hi}%</span>
                    <span className="ml-auto flex items-center">
                      {inRange ? (
                        <CheckCircleIcon className="h-5 w-5 text-emerald-500" aria-label={`${TIER_META[t].short} on track`} />
                      ) : (
                        <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                          {tierStatus(tierTotals[t], TIER_TARGETS[t]).label}
                        </span>
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 border-t border-gray-200 bg-gray-50 px-6 py-5 dark:border-gray-800 dark:bg-gray-900/50 sm:px-8 rounded-b-2xl">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <ArrowDownTrayIcon className="h-5 w-5" /> Download CSV
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className="h-5 w-5" /> Reset
          </button>
        </div>
      </div>

      {/* Cross-link to the downloadable template */}
      <div className="mt-6 rounded-2xl bg-indigo-50 p-6 ring-1 ring-indigo-100 dark:bg-indigo-900/20 dark:ring-indigo-900/40">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Want to track planned vs. actual spend?</h3>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Export your allocation above, then drop it into our{' '}
          <Link href="/resources/templates/marketing-budget-template" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400">
            Marketing Budget Template
          </Link>{' '}
          to track pacing, actuals, and ROAS by channel all year.
        </p>
      </div>

      {/* Related concepts — SSR-visible cross-links (slugs guarded by
          related-link-slugs.test.ts via MarketingBudgetCalculator.related.ts). */}
      <section
        aria-labelledby="budget-related-concepts"
        className="mt-8 rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-8"
      >
        <h3 id="budget-related-concepts" className="mb-3 text-2xl font-semibold text-gray-900 dark:text-white">
          Related concepts &amp; calculators
        </h3>
        <p className="mb-6 text-gray-600 dark:text-gray-400">
          A budget is only as good as the efficiency metrics that steer it. Use these definitions and calculators to set
          allocations from real economics — channel returns, blended efficiency, acquisition cost, and lifetime value.
        </p>
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Glossary terms</h4>
            <ul className="space-y-3">
              {RELATED_GLOSSARY.map((item) => (
                <li key={item.slug} className="text-sm">
                  <Link href={`/resources/glossary/${item.slug}`} className="font-medium text-blue-600 hover:underline dark:text-blue-400">
                    {item.label}
                  </Link>
                  <span className="text-gray-600 dark:text-gray-400"> — {item.note}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Other calculators</h4>
            <ul className="space-y-3">
              {RELATED_TOOLS.map((item) => (
                <li key={item.slug} className="text-sm">
                  <Link href={`/resources/tools/calculators/${item.slug}`} className="font-medium text-blue-600 hover:underline dark:text-blue-400">
                    {item.label}
                  </Link>
                  <span className="text-gray-600 dark:text-gray-400"> — {item.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="mt-8 rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-8">
        <h3 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-white">Frequently Asked Questions</h3>
        <FAQ items={FAQ_ITEMS} type="single" disableSchema={true} features={{ showTitle: false }} theme={{ size: 'xl' }} />
      </div>
    </div>
  )
}
